// ============================================================================
// FACET-ENTITY GRAPH HOOKS
// React Query hooks for facet-entity linking operations
// Enables many-to-one relationships: multiple facets → one entity
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sourcesApi } from '@/lib/api/client'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'

// ============================================================================
// TYPES
// ============================================================================

export interface ProblemStatement {
  context: string      // "When... [specific situation]"
  cause: string        // "This can cause... [negative impact]"
  consequence: string  // "This is problematic because... [impact]"
}

export interface LinkedFacet {
  uri: string
  facetType: string
  title?: string        // Short descriptive name (5-10 words)
  content: string       // Summary (1-2 sentences)
  evidence: string
  confidence: number
  problemStatement?: ProblemStatement  // Three C's for problem-type facets
  sourceUri?: string
  sourceTitle?: string
  status?: string
  createdAt?: string
}

export interface EntitySearchResult {
  uri: string
  label: string
  type: string
  facetCount: number
}

// ============================================================================
// QUERIES
// ============================================================================

/**
 * Get all facets linked to a specific entity
 * Useful for showing evidence in entity detail views
 */
export function useLinkedFacets(entityUri: string | null) {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  return useQuery({
    queryKey: ['linkedFacets', entityUri],
    queryFn: async () => {
      if (!currentAccount || !currentProject || !entityUri) return null

      const result = await sourcesApi.getFacetsForEntity(
        currentAccount.id,
        currentProject.id,
        entityUri
      )

      return {
        facets: result.facets,
        entityUri: result.entityUri,
        count: result.facets.length,
      }
    },
    enabled: !!entityUri && !!currentAccount && !!currentProject,
  })
}

/**
 * Search entities with facet counts for linking UI
 * Shows how many facets already describe each entity
 */
export function useSearchEntitiesForLinking(
  searchTerm: string,
  entityType?: string,
  limit = 20
) {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  return useQuery({
    queryKey: ['entitiesForLinking', searchTerm, entityType, limit],
    queryFn: async () => {
      if (!currentAccount || !currentProject || !searchTerm) return []

      const result = await sourcesApi.searchEntitiesForLinking(
        currentAccount.id,
        currentProject.id,
        searchTerm,
        entityType,
        limit
      )

      return result.entities
    },
    enabled: !!searchTerm && searchTerm.length >= 2 && !!currentAccount && !!currentProject,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Link a single facet to an existing entity
 * Creates facet node in graph if needed, then links to entity
 */
export function useLinkFacetToEntity() {
  const queryClient = useQueryClient()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  return useMutation({
    mutationFn: async ({
      sourceId,
      facetId,
      entityUri,
    }: {
      sourceId: string
      facetId: string
      entityUri: string
    }) => {
      if (!currentAccount || !currentProject) {
        throw new Error('No account or project selected')
      }

      return sourcesApi.linkFacetToEntity(
        currentAccount.id,
        currentProject.id,
        sourceId,
        facetId,
        entityUri
      )
    },
    onSuccess: (_, { entityUri }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['linkedFacets', entityUri] })
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['entitiesForLinking'] })
      queryClient.invalidateQueries({ queryKey: ['instanceDetails'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

/**
 * Bulk link multiple facets to one entity
 * Efficient for merging multiple facets describing the same thing
 */
export function useBulkLinkFacets() {
  const queryClient = useQueryClient()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  return useMutation({
    mutationFn: async ({
      sourceId,
      facetIds,
      entityUri,
    }: {
      sourceId: string
      facetIds: string[]
      entityUri: string
    }) => {
      if (!currentAccount || !currentProject) {
        throw new Error('No account or project selected')
      }

      return sourcesApi.bulkLinkFacets(
        currentAccount.id,
        currentProject.id,
        sourceId,
        facetIds,
        entityUri
      )
    },
    onSuccess: (_, { entityUri }) => {
      queryClient.invalidateQueries({ queryKey: ['linkedFacets', entityUri] })
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['entitiesForLinking'] })
      queryClient.invalidateQueries({ queryKey: ['instanceDetails'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

/**
 * Unlink a facet from its entity
 * Facet remains in graph, just removes the describesEntity link
 */
export function useUnlinkFacet() {
  const queryClient = useQueryClient()
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()

  return useMutation({
    mutationFn: async ({
      sourceId,
      facetId,
      entityUri,
    }: {
      sourceId: string
      facetId: string
      entityUri?: string // Optional, used for cache invalidation
    }) => {
      if (!currentAccount || !currentProject) {
        throw new Error('No account or project selected')
      }

      return sourcesApi.unlinkFacet(
        currentAccount.id,
        currentProject.id,
        sourceId,
        facetId
      )
    },
    onSuccess: (_, { entityUri }) => {
      if (entityUri) {
        queryClient.invalidateQueries({ queryKey: ['linkedFacets', entityUri] })
      }
      queryClient.invalidateQueries({ queryKey: ['sources'] })
      queryClient.invalidateQueries({ queryKey: ['entitiesForLinking'] })
      queryClient.invalidateQueries({ queryKey: ['instanceDetails'] })
      queryClient.invalidateQueries({ queryKey: ['graphData'] })
    },
  })
}

// ============================================================================
// COMBINED HOOKS FOR COMMON USE CASES
// ============================================================================

/**
 * Hook for entity detail panels - provides linked facets with loading state
 */
export function useEntityFacets(entityUri: string | null) {
  const { data, isLoading, error, refetch } = useLinkedFacets(entityUri)
  const unlinkMutation = useUnlinkFacet()

  return {
    facets: data?.facets ?? [],
    count: data?.count ?? 0,
    isLoading,
    error,
    refetch,
    unlinkFacet: unlinkMutation.mutate,
    isUnlinking: unlinkMutation.isPending,
  }
}

/**
 * Hook for linking UI - provides search and link capabilities
 */
export function useFacetLinking(sourceId: string) {
  const linkMutation = useLinkFacetToEntity()
  const bulkLinkMutation = useBulkLinkFacets()

  return {
    linkToEntity: (facetId: string, entityUri: string) =>
      linkMutation.mutateAsync({ sourceId, facetId, entityUri }),
    bulkLinkToEntity: (facetIds: string[], entityUri: string) =>
      bulkLinkMutation.mutateAsync({ sourceId, facetIds, entityUri }),
    isLinking: linkMutation.isPending || bulkLinkMutation.isPending,
    linkError: linkMutation.error || bulkLinkMutation.error,
  }
}
